import { useCan } from '@/components/can/Can';
import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDialogFormStore } from '@/components/dialog/form.store';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { useDynamicLocale } from '@/hoslog-intl';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { PromotionalBanner } from '@/services/promotionalBanner/promotionalBanner.types';
import * as React from 'react';
import { BannerManagementForm } from '../BannerManagementForm';
import useDeletePromotionalBanner from '../hooks/useDeletePromotionalBanner';
import useUpdatePromotionalBannerStatus from '../hooks/useUpdatePromotionalBannerStatus';
import { getPromotionBannerColumns } from './Column';

export const PromotionalBannerTable = ({
  data,
  pagination,
  loading,
}: {
  data: PromotionalBanner[];
  pagination: Pagination;
  loading: boolean;
}) => {
  const { onOpen } = useDialogFormStore();
  const { t, locale } = useDynamicLocale();

  const { openConfirmation } = useConfirmationStore();
  const canAddBanner = useCan({ allOf: [PERMS.promotionalBanner.add] });
  const canEditBanner = useCan({ allOf: [PERMS.promotionalBanner.update] });
  const { mutateAsync } = useUpdatePromotionalBannerStatus();
  const { mutateAsync: deleteMutation } = useDeletePromotionalBanner();
  const columns = React.useMemo(
    () => getPromotionBannerColumns(t, locale, openConfirmation, onOpen, canEditBanner, mutateAsync, deleteMutation),
    [t, locale, openConfirmation, onOpen, canEditBanner, mutateAsync, deleteMutation],
  );

  const onAddNew = () => {
    onOpen({
      modalTitle: 'promoManagement.promotionalBanners.modal.title',
      title: 'Manage Api Response Messages',
      okText: t('common.page.button.submit'),
      size: 'lg',
      cancelText: t('common.page.button.cancel'),
      component: <BannerManagementForm />,
    });
  };

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const { table } = useDataTable({
    data,
    columns,
    pageCount,
    meta: {
      title: t('promoManagement.promotionalBanners.table.title'),
      totalPage: pagination.totalItems,
      loading,
      action: canAddBanner && onAddNew,
      actionKey: canAddBanner ? 'onAddNew' : '',
    },
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <div>
      <DataTable table={table} className="rounded-t-none rounded-b-xl">
        <DataTableToolbar />
      </DataTable>
    </div>
  );
};
